<<template>
  <div>
    <v-dialog
      v-model="showWelcomeDialog"
      max-width="1100"
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
      content-class="organic-dialog"
    >
      <v-card class="organic-card" flat color="#fdfcf8">
        <!-- Header organique avec forme fluide -->
        <div class="organic-header">
          <div class="header-blob" />
          <v-btn
            icon
            dark
            absolute
            top
            right
            class="mt-4 mr-4 close-btn-organic"
            @click="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <div class="header-content-organic px-6 pt-8 pb-10 text-center">
            <div class="logo-blob mb-4">
              <v-icon size="32" color="white">mdi-rocket-launch</v-icon>
            </div>
            <h1 class="text-h5 text-sm-h4 font-weight-bold white--text mb-2">
              Welcome to Njangi
            </h1>
            <p class="text-body-2 white--text text--lighten-3 font-weight-light">
              Community savings reimagined
            </p>
          </div>
        </div>

        <!-- Navigation organique (gouttes) -->
        <div class="organic-nav py-4 px-6">
          <div class="nav-drops">
            <button
              v-for="n in 3"
              :key="n"
              class="nav-drop"
              :class="{ 'nav-drop--active': currentStep === n }"
              @click="currentStep = n"
            >
              <span class="nav-drop__text">{{ ['Discover', 'Create', 'Manage'][n-1] }}</span>
            </button>
          </div>
          <div class="nav-line">
            <div class="nav-line__fill" :style="{ width: (currentStep / 3) * 100 + '%' }" />
          </div>
        </div>

        <!-- Contenu avec transition douce -->
        <v-card-text class="pa-0">
          <v-scroll-x-transition mode="out-in" hide-on-leave>
            <div :key="currentStep" class="organic-step">
              
              <!-- ÉTAPE 1 : Discover -->
              <div v-if="currentStep === 1" class="step-body pa-4 pa-sm-6">
                <v-row dense>
                  <!-- Left: Hero visuel -->
                  <v-col cols="12" md="5" class="mb-4 mb-md-0">
                    <div class="hero-block">
                      <div class="hero-visual">
                        <div class="hero-circle hero-circle--big">
                          <span class="hero-amount">50K</span>
                          <span class="hero-unit">FCFA</span>
                        </div>
                        <div class="hero-orbit">
                          <div class="orbit-dot" style="--delay: 0s"><v-icon x-small color="white">mdi-account</v-icon></div>
                          <div class="orbit-dot" style="--delay: 0.5s"><v-icon x-small color="white">mdi-account</v-icon></div>
                          <div class="orbit-dot" style="--delay: 1s"><v-icon x-small color="white">mdi-account</v-icon></div>
                          <div class="orbit-dot" style="--delay: 1.5s"><v-icon x-small color="white">mdi-account</v-icon></div>
                        </div>
                      </div>
                      <div class="hero-caption text-center mt-4">
                        <div class="text-h6 font-weight-bold terracotta--text">10 Members</div>
                        <div class="text-body-2 grey--text">× 5,000 FCFA weekly</div>
                      </div>
                    </div>

                    <v-card class="explain-card mt-4 pa-4 rounded-2xl" flat outlined>
                      <div class="d-flex align-start">
                        <v-avatar size="36" color="sage" class="mr-3 flex-shrink-0">
                          <v-icon dark size="18">mdi-lightbulb-on</v-icon>
                        </v-avatar>
                        <div>
                          <div class="text-body-2 font-weight-bold grey--text text--darken-2">How it works</div>
                          <div class="text-caption grey--text text--darken-1 mt-1">
                            Members contribute regularly and each week, one person collects the full pool. Everyone gets their turn.
                          </div>
                        </div>
                      </div>
                    </v-card>
                  </v-col>

                  <!-- Right: Bento Grid -->
                  <v-col cols="12" md="7">
                    <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-3 d-flex align-center">
                      <span class="section-icon mr-2">✦</span>
                      Why Njangi?
                    </h3>

                    <div class="bento-grid">
                      <div
                        v-for="(feature, i) in features"
                        :key="i"
                        class="bento-item"
                        :class="{ 'bento-item--wide': i === 0 || i === 3 }"
                        :style="{ '--accent': feature.hex }"
                      >
                        <div class="bento-item__icon">
                          <v-icon :color="feature.color">{{ feature.icon }}</v-icon>
                        </div>
                        <div class="bento-item__title">{{ feature.title }}</div>
                        <div class="bento-item__desc">{{ feature.description }}</div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- ÉTAPE 2 : Create -->
              <div v-if="currentStep === 2" class="step-body pa-4 pa-sm-6">
                <v-row dense>
                  <!-- Left: Action Card -->
                  <v-col cols="12" md="5" class="mb-4 mb-md-0">
                    <v-card class="create-card-organic pa-5 rounded-3xl text-center" flat>
                      <div class="create-illustration mb-4">
                        <div class="illus-circle illus-circle--1" />
                        <div class="illus-circle illus-circle--2" />
                        <div class="illus-circle illus-circle--3" />
                        <v-icon size="40" color="white" class="illus-icon">mdi-plus-circle</v-icon>
                      </div>

                      <h2 class="text-h6 font-weight-bold grey--text text--darken-3 mb-2">
                        Start Your Group
                      </h2>
                      <p class="text-body-2 grey--text text--darken-1 mb-5">
                        Create in 3 simple steps and invite your trusted circle
                      </p>

                      <div class="create-steps-organic mb-5">
                        <div
                          v-for="(step, i) in createSteps"
                          :key="i"
                          class="create-step-row"
                        >
                          <div class="create-step-row__num" :style="{ background: step.hex }">
                            {{ i + 1 }}
                          </div>
                          <div class="create-step-row__text">
                            <div class="text-body-2 font-weight-medium grey--text text--darken-2">{{ step.title }}</div>
                            <div class="text-caption grey--text">{{ step.description }}</div>
                          </div>
                        </div>
                      </div>

                      <v-btn
                        block
                        color="terracotta"
                        dark
                        class="rounded-xl font-weight-bold text-none"
                        height="52"
                        x-large
                        to="/business-account/dashboard/create-njangi-group"
                        @click="closeDialog"
                      >
                        <v-icon left>mdi-rocket-launch</v-icon>
                        Create Group
                      </v-btn>
                      <div class="text-caption grey--text text--lighten-1 mt-3">
                        <v-icon x-small>mdi-clock-fast</v-icon> Takes less than 4 minutes
                      </div>
                    </v-card>
                  </v-col>

                  <!-- Right: Capabilities -->
                  <v-col cols="12" md="7">
                    <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-3">
                      <span class="section-icon mr-2">⚡</span>
                      Your Powers
                    </h3>

                    <div class="powers-list">
                      <v-card
                        v-for="(cap, i) in capabilities"
                        :key="i"
                        class="power-card pa-4 rounded-2xl mb-3"
                        flat
                        outlined
                      >
                        <div class="d-flex align-center">
                          <div class="power-icon-wrap" :style="{ background: cap.hex }">
                            <v-icon color="white">{{ cap.icon }}</v-icon>
                          </div>
                          <div class="ml-4">
                            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2">{{ cap.title }}</div>
                            <div class="text-caption grey--text text--darken-1">{{ cap.description }}</div>
                          </div>
                          <v-spacer />
                          <v-icon small color="grey lighten-1">mdi-chevron-right</v-icon>
                        </div>
                      </v-card>
                    </div>
                  </v-col>
                </v-row>
              </div>

              <!-- ÉTAPE 3 : Manage -->
              <div v-if="currentStep === 3" class="step-body pa-4 pa-sm-6">
                <v-row dense>
                  <!-- Left: Dashboard Card -->
                  <v-col cols="12" md="5" class="mb-4 mb-md-0">
                    <v-card class="manage-card-organic pa-5 rounded-3xl" flat>
                      <div class="text-center mb-5">
                        <div class="manage-avatar mb-3">
                          <v-icon size="36" color="white">mdi-shield-account</v-icon>
                        </div>
                        <h2 class="text-h6 font-weight-bold grey--text text--darken-3">Admin Dashboard</h2>
                        <p class="text-body-2 grey--text text--darken-1">Full control over your groups</p>
                      </div>

                      <div class="manage-stats mb-5">
                        <div class="stat-pill">
                          <div class="stat-pill__num">12</div>
                          <div class="stat-pill__label">Members</div>
                        </div>
                        <div class="stat-pill">
                          <div class="stat-pill__num">3</div>
                          <div class="stat-pill__label">Groups</div>
                        </div>
                        <div class="stat-pill">
                          <div class="stat-pill__num">500K</div>
                          <div class="stat-pill__label">Saved</div>
                        </div>
                      </div>

                      <div class="manage-list mb-5">
                        <div
                          v-for="(feature, i) in adminFeatures"
                          :key="i"
                          class="manage-row"
                        >
                          <v-icon :color="feature.color" size="20" class="mr-3">{{ feature.icon }}</v-icon>
                          <div class="text-body-2 grey--text text--darken-2">{{ feature.title }}</div>
                        </div>
                      </div>

                      <v-btn
                        block
                        color="slate"
                        dark
                        class="rounded-xl font-weight-bold text-none"
                        height="48"
                        to="/business-account/dashboard/njangi/admin/groups"
                        @click="closeDialog"
                      >
                        <v-icon left>mdi-view-dashboard</v-icon>
                        Open Dashboard
                      </v-btn>
                    </v-card>
                  </v-col>

                  <!-- Right: Pro Tips -->
                  <v-col cols="12" md="7">
                    <h3 class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-3">
                      <span class="section-icon mr-2">★</span>
                      Pro Tips
                    </h3>

                    <div class="tips-organic">
                      <v-card
                        v-for="(tip, i) in proTips"
                        :key="i"
                        class="tip-organic-card pa-4 rounded-2xl mb-3"
                        flat
                        outlined
                      >
                        <div class="d-flex align-start">
                          <div class="tip-num" :style="{ background: tip.hex }">{{ i + 1 }}</div>
                          <div class="ml-3 flex-grow-1">
                            <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2 mb-1">{{ tip.title }}</div>
                            <div class="text-caption grey--text text--darken-1">{{ tip.description }}</div>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-scroll-x-transition>
        </v-card-text>

        <!-- Footer organique -->
        <div class="organic-footer px-6 py-4">
          <div class="footer-inner">
            <v-btn
              text
              color="grey darken-1"
              :disabled="currentStep === 1"
              @click="currentStep--"
              class="text-none font-weight-medium rounded-xl"
              height="44"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>

            <div class="footer-dots">
              <div
                v-for="n in 3"
                :key="n"
                class="f-dot"
                :class="{ 'f-dot--active': currentStep === n }"
              />

            </div>

            <v-btn
              v-if="currentStep < 3"
              color="terracotta"
              dark
              class="text-none font-weight-bold rounded-xl px-6"
              height="44"
              @click="currentStep++"
            >
              Next
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>

            <v-btn
              v-else
              color="sage darken-1"
              dark
              class="text-none font-weight-bold rounded-xl px-6"
              height="44"
              @click="closeDialog"
            >
              <v-icon left>mdi-check</v-icon>
              Get Started
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'NjangiWelcomePopup',

  data() {
    return {
      showWelcomeDialog: false,
      currentStep: 1,
      checkInterval: null,
      hasChecked: false,
      popupVersion: 2,

      features: [
        {
          icon: 'mdi-cash-multiple',
          title: 'Collective Savings',
          description: 'Pool resources for bigger goals',
          color: 'green darken-2',
          hex: '#2d6a4f'
        },
        {
          icon: 'mdi-shield-check-outline',
          title: 'Fully Transparent',
          description: 'Real-time tracking for all',
          color: 'blue darken-2',
          hex: '#1d3557'
        },
        {
          icon: 'mdi-account-multiple-outline',
          title: 'Community First',
          description: 'Trust-based financial circles',
          color: 'purple darken-2',
          hex: '#6d597a'
        },
        {
          icon: 'mdi-calendar-sync-outline',
          title: 'Flexible Cycles',
          description: 'Weekly, monthly or daily',
          color: 'orange darken-2',
          hex: '#e76f51'
        }
      ],

      createSteps: [
        {
          title: 'Accept Terms',
          description: 'Review your admin responsibilities',
          color: 'terracotta',
          hex: '#e07a5f'
        },
        {
          title: 'Configure Group',
          description: 'Set name, amount & collection cycle',
          color: 'sage',
          hex: '#81b29a'
        },
        {
          title: 'Invite Members',
          description: 'Share via WhatsApp or username',
          color: 'sand',
          hex: '#f2cc8f'
        }
      ],

      capabilities: [
        {
          icon: 'mdi-image-outline',
          title: 'Group Photo',
          description: 'Personalize with a custom image',
          color: 'pink',
          hex: '#e07a5f'
        },
        {
          icon: 'mdi-cash-check',
          title: 'Set Contributions',
          description: 'Define affordable amounts',
          color: 'green',
          hex: '#2d6a4f'
        },
        {
          icon: 'mdi-calendar-clock',
          title: 'Pick Schedule',
          description: 'Daily, weekly or monthly',
          color: 'blue',
          hex: '#1d3557'
        },
        {
          icon: 'mdi-whatsapp',
          title: 'Easy Invites',
          description: 'One-tap WhatsApp sharing',
          color: 'green darken-3',
          hex: '#2d6a4f'
        }
      ],

      adminFeatures: [
        {
          icon: 'mdi-account-multiple-outline',
          title: 'View all members & positions',
          color: 'blue'
        },
        {
          icon: 'mdi-drag-vertical',
          title: 'Drag to reorder collection',
          color: 'orange'
        },
        {
          icon: 'mdi-account-remove-outline',
          title: 'Remove inactive members',
          color: 'red'
        },
        {
          icon: 'mdi-share-variant-outline',
          title: 'Share invite links anytime',
          color: 'green'
        }
      ],

      proTips: [
        {
          icon: 'mdi-account-heart-outline',
          title: 'Start with Trust',
          description: 'Only invite people you know personally and can vouch for financially.',
          color: 'pink',
          hex: '#e07a5f'
        },
        {
          icon: 'mdi-cash-check',
          title: 'Keep it Realistic',
          description: 'Set contribution amounts that everyone can afford without stress.',
          color: 'green',
          hex: '#2d6a4f'
        },
        {
          icon: 'mdi-message-text-outline',
          title: 'Over-Communicate',
          description: 'Send reminders before collections. Silence kills groups.',
          color: 'blue',
          hex: '#1d3557'
        },
        {
          icon: 'mdi-gavel',
          title: 'Enforce Fairly',
          description: 'Apply rules consistently. No exceptions, no drama.',
          color: 'orange',
          hex: '#e76f51'
        },
        {
          icon: 'mdi-file-document-outline',
          title: 'Document Everything',
          description: 'Keep records of all contributions and decisions.',
          color: 'purple',
          hex: '#6d597a'
        }
      ]
    };
  },

  computed: {
    isOnNjangiRoute() {
      const path = this.$route.path;
      return path.includes('/business-account') || path.includes('/njangi');
    },

    currentUserId() {
      return this.$store.state.user_info?.user?.id ||
             this.$store.state.username ||
             localStorage.getItem('userid') ||
             localStorage.getItem('username') ||
             null;
    },

    hasSeenCurrentVersion() {
      return this.$store.getters.njangiWelcomeSeenForCurrentUser;
    },

    storeVersion() {
      return this.$store.getters.njangiWelcomeCurrentVersion;
    }
  },

  mounted() {
    console.log(`NjangiWelcomePopup mounted - v${this.popupVersion}, Store v${this.storeVersion}`);
    this.waitForUserInfoAndShow();
  },

  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },

  watch: {
    '$route'() {
      if (!this.hasChecked && this.isOnNjangiRoute) {
        this.waitForUserInfoAndShow();
      }
    },

    '$store.state.user_info_state'(newVal) {
      if (newVal && !this.hasChecked && this.isOnNjangiRoute) {
        this.checkAndShowPopup();
      }
    }
  },

  methods: {
    waitForUserInfoAndShow() {
      if (this.$store.state.user_info_state && this.currentUserId) {
        this.checkAndShowPopup();
        return;
      }

      let attempts = 0;
      this.checkInterval = setInterval(() => {
        attempts++;
        if (this.$store.state.user_info_state && this.currentUserId) {
          clearInterval(this.checkInterval);
          this.checkAndShowPopup();
        } else if (attempts > 50) {
          clearInterval(this.checkInterval);
          if (this.currentUserId) {
            this.checkAndShowPopup();
          }
        }
      }, 100);
    },

    checkAndShowPopup() {
      if (this.hasChecked) return;
      this.hasChecked = true;
      if (!this.isOnNjangiRoute) return;

      const userId = this.currentUserId;
      if (!userId) return;

      console.log(`Checking popup for user ${userId}: seen=${this.hasSeenCurrentVersion}`);

      if (!this.hasSeenCurrentVersion) {
        console.log(`Showing popup v${this.popupVersion} for user ${userId}`);
        setTimeout(() => {
          this.showWelcomeDialog = true;
        }, 500);
      } else {
        console.log(`User ${userId} has already seen v${this.popupVersion}`);
      }
    },

    closeDialog() {
      this.showWelcomeDialog = false;
      const userId = this.currentUserId;
      if (userId) {
        console.log(`Marking popup v${this.popupVersion} as seen for user ${userId}`);
        this.$store.commit('setNjangiWelcomeSeenForUser', { userId });
      }
    }
  }
};
</script>

<style scoped>
/* ===== PALETTE & VARIABLES ===== */
.terracotta--text { color: #e07a5f !important; }
.bg-terracotta { background-color: #e07a5f !important; }
.bg-sage { background-color: #81b29a !important; }
.bg-sand { background-color: #f2cc8f !important; }
.bg-slate { background-color: #3d405b !important; }

/* ===== DIALOG ===== */
.organic-dialog {
  border-radius: 32px !important;
  overflow: hidden;
  box-shadow: 0 32px 64px -12px rgba(61, 64, 91, 0.25) !important;
}

@media (max-width: 600px) {
  .organic-dialog {
    border-radius: 0 !important;
    margin: 0 !important;
    height: 100vh;
    max-height: 100vh !important;
  }
}

.organic-card {
  border-radius: 32px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

@media (max-width: 600px) {
  .organic-card {
    border-radius: 0 !important;
    height: 100vh;
  }
}

/* ===== HEADER ORGANIQUE ===== */
.organic-header {
  background: linear-gradient(135deg, #e07a5f 0%, #e29578 50%, #f2cc8f 100%);
  position: relative;
  overflow: hidden;
  border-radius: 0 0 48px 48px;
  padding-bottom: 16px;
}

.header-blob {
  position: absolute;
  top: -60%;
  right: -20%;
  width: 400px;
  height: 400px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
  animation: blob-morph 8s ease-in-out infinite;
}

@keyframes blob-morph {
  0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: rotate(0deg); }
  50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; transform: rotate(180deg); }
}

.header-content-organic {
  position: relative;
  z-index: 1;
}

.logo-blob {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(8px);
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.close-btn-organic {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;
}

.close-btn-organic:hover {
  background: rgba(255, 255, 255, 0.35) !important;
  transform: rotate(90deg) scale(1.1);
}

/* ===== NAVIGATION GOUTTES ===== */
.organic-nav {
  background: white;
}

.nav-drops {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.nav-drop {
  border: none;
  background: #f1f1f1;
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #9e9e9e;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-drop:hover {
  background: #e8e8e8;
  transform: translateY(-2px);
}

.nav-drop--active {
  background: #3d405b !important;
  color: white !important;
  box-shadow: 0 4px 16px rgba(61, 64, 91, 0.3);
  transform: scale(1.05);
}

.nav-line {
  height: 3px;
  background: #f1f1f1;
  border-radius: 3px;
  overflow: hidden;
  position: relative;
}

.nav-line__fill {
  height: 100%;
  background: linear-gradient(90deg, #e07a5f, #f2cc8f);
  border-radius: 3px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

/* ===== CONTENU ===== */
.organic-step {
  min-height: 400px;
}

.step-body {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

@media (min-width: 600px) {
  .step-body {
    max-height: 460px;
  }
}

@media (min-width: 960px) {
  .step-body {
    max-height: 500px;
  }
}

/* Scrollbar douce */
.step-body::-webkit-scrollbar {
  width: 6px;
}

.step-body::-webkit-scrollbar-track {
  background: transparent;
}

.step-body::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

/* ===== HERO VISUEL CIRCULAIRE ===== */
.hero-block {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-visual {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-circle {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e07a5f, #f2cc8f);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 40px rgba(224, 122, 95, 0.3);
  position: relative;
  z-index: 2;
}

.hero-amount {
  font-size: 2.5rem;
  font-weight: 900;
  color: white;
  line-height: 1;
}

.hero-unit {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.hero-orbit {
  position: absolute;
  width: 100%;
  height: 100%;
  animation: orbit-spin 20s linear infinite;
}

.orbit-dot {
  position: absolute;
  width: 28px;
  height: 28px;
  background: #3d405b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.orbit-dot:nth-child(1) { top: 0; left: 50%; transform: translateX(-50%); }
.orbit-dot:nth-child(2) { top: 50%; right: 0; transform: translateY(-50%); }
.orbit-dot:nth-child(3) { bottom: 0; left: 50%; transform: translateX(-50%); }
.orbit-dot:nth-child(4) { top: 50%; left: 0; transform: translateY(-50%); }

@keyframes orbit-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.hero-caption {
  text-align: center;
}

/* ===== EXPLAIN CARD ===== */
.explain-card {
  background: #fff8f5 !important;
  border-color: rgba(224, 122, 95, 0.15) !important;
}

/* ===== BENTO GRID ===== */
.bento-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.bento-item {
  background: white;
  border-radius: 20px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.bento-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.1);
}

.bento-item--wide {
  grid-column: span 2;
}

.bento-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: var(--accent);
  opacity: 0.7;
}

.bento-item__icon {
  margin-bottom: 10px;
}

.bento-item__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #3d405b;
  margin-bottom: 4px;
}

.bento-item__desc {
  font-size: 0.75rem;
  color: #9e9e9e;
  line-height: 1.4;
}

/* ===== CREATE CARD ===== */
.create-card-organic {
  background: linear-gradient(180deg, #fff8f5 0%, #ffffff 100%) !important;
  border-top: 4px solid #e07a5f;
}

.create-illustration {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.illus-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.6;
}

.illus-circle--1 {
  width: 100px;
  height: 100px;
  background: #e07a5f;
  animation: pulse-ring 2s ease-out infinite;
}

.illus-circle--2 {
  width: 70px;
  height: 70px;
  background: #f2cc8f;
  animation: pulse-ring 2s ease-out 0.5s infinite;
}

.illus-circle--3 {
  width: 40px;
  height: 40px;
  background: #81b29a;
  animation: pulse-ring 2s ease-out 1s infinite;
}

.illus-icon {
  position: relative;
  z-index: 2;
}

@keyframes pulse-ring {
  0% { transform: scale(0.8); opacity: 0.6; }
  100% { transform: scale(1.3); opacity: 0; }
}

.create-steps-organic {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.create-step-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.create-step-row__num {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.create-step-row__text {
  flex: 1;
  text-align: left;
}

/* ===== POWERS LIST ===== */
.powers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.power-card {
  transition: all 0.2s ease;
  background: white;
}

.power-card:hover {
  transform: translateX(4px);
  border-color: rgba(0, 0, 0, 0.12);
}

.power-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ===== MANAGE CARD ===== */
.manage-card-organic {
  background: linear-gradient(180deg, #f0f4f8 0%, #ffffff 100%) !important;
  border-top: 4px solid #3d405b;
}

.manage-avatar {
  width: 64px;
  height: 64px;
  background: #3d405b;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(61, 64, 91, 0.25);
}

.manage-stats {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.stat-pill {
  background: white;
  border-radius: 16px;
  padding: 12px 16px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.06);
  flex: 1;
}

.stat-pill__num {
  font-size: 1.25rem;
  font-weight: 800;
  color: #3d405b;
  line-height: 1;
}

.stat-pill__label {
  font-size: 0.7rem;
  color: #9e9e9e;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.manage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.manage-row {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: white;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s;
}

.manage-row:hover {
  transform: translateX(2px);
  border-color: rgba(0, 0, 0, 0.1);
}

/* ===== TIPS ===== */
.tips-organic {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tip-organic-card {
  transition: all 0.2s ease;
  background: white;
  border-left: 3px solid transparent;
}

.tip-organic-card:hover {
  border-left-color: var(--accent, #e07a5f);
  transform: translateX(3px);
}

.tip-num {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

/* ===== FOOTER ===== */
.organic-footer {
  background: white;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 600px;
  margin: 0 auto;
}

.footer-dots {
  display: flex;
  gap: 8px;
}

.f-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.3s ease;
}

.f-dot--active {
  background: #e07a5f;
  width: 24px;
  border-radius: 4px;
}

/* ===== UTILS ===== */
.section-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.rounded-2xl {
  border-radius: 20px !important;
}

.rounded-3xl {
  border-radius: 24px !important;
}

/* ===== MOBILE ===== */
@media (max-width: 600px) {
  .organic-header {
    border-radius: 0 0 32px 32px;
    padding-bottom: 12px;
  }
  
  .header-content-organic {
    padding-top: 24px !important;
    padding-bottom: 24px !important;
  }
  
  .hero-visual {
    width: 160px;
    height: 160px;
  }
  
  .hero-circle {
    width: 130px;
    height: 130px;
  }
  
  .hero-amount {
    font-size: 2rem;
  }
  
  .bento-grid {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  
  .bento-item {
    padding: 12px;
  }
  
  .step-body {
    padding: 16px 12px !important;
    max-height: calc(100vh - 260px);
  }
  
  .nav-drop {
    padding: 6px 14px;
    font-size: 0.75rem;
  }
  
  .footer-inner {
    gap: 8px;
  }
}

/* ===== DESKTOP LARGE ===== */
@media (min-width: 1264px) {
  .organic-dialog {
    max-width: 1200px !important;
  }
  
  .step-body {
    max-height: 540px;
    padding: 32px 40px;
  }
}
</style>